import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { NuevoContratoModal } from '../MantenimientosView';

const PM_STATE = 1069430;

export default function TechnicianContratosView() {
  const { user } = useAuth();

  // Búsqueda inline
  const [query,     setQuery]     = useState('');
  const [searching, setSearching] = useState(false);
  const [results,   setResults]   = useState([]);
  const searchTimer = useRef(null);

  // Puestas en marcha del día
  const [pmOrders,  setPmOrders]  = useState([]);
  const [pmLoading, setPmLoading] = useState(true);

  // Modal
  const [modalConfig, setModalConfig] = useState(null); // null | { clienteInicial, workorderRef }

  useEffect(() => {
    if (!user?.employeeId) return;
    const today = new Date().toISOString().split('T')[0];
    fetch(`/.netlify/functions/getTechnicianOrders?employeeId=${user.employeeId}&date=${today}`, { headers: authService.authHeader() })
      .then(r => r.json())
      .then(d => { if (d.success) setPmOrders((d.orders || []).filter(o => o['document-state-id'] === PM_STATE)); })
      .catch(() => {})
      .finally(() => setPmLoading(false));
  }, [user?.employeeId]);

  const buscarClientes = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await fetch(`/.netlify/functions/searchClienteStelOrder?q=${encodeURIComponent(q.trim())}`, { headers: { ...authService.authHeader() } });
      const d = await r.json();
      setResults(d.success ? d.results : []);
    } catch { setResults([]); }
    finally   { setSearching(false); }
  }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => buscarClientes(val), 500);
  };

  const seleccionarCliente = (c) => {
    setResults([]);
    setQuery('');
    setModalConfig({
      clienteInicial: {
        clienteId:       String(c.id || ''),
        addressId:       String(c.addressId || ''),
        clienteNombre:   c.clienteNombre || '',
        clienteTelefono: c.phone || '',
        clienteEmail:    c.email || '',
        municipio:       c.municipio || '',
        direccion:       c.direccion || '',
      },
      workorderRef: '',
    });
  };

  const abrirDesdeOrden = (order) => {
    const eq = order.equipmentInfo || '';
    setModalConfig({
      clienteInicial: {
        clienteId:       String(order['account-id'] || ''),
        addressId:       String(order['work-address-id'] || ''),
        clienteNombre:   order.clientName || '',
        clienteTelefono: order.clientPhone || '',
        clienteEmail:    '',
        municipio:       '',
        direccion:       order.addressText || '',
        marca:           eq.split(' ')[0] || '',
        modelo:          eq.split(' ').slice(1).join(' ') || '',
      },
      workorderRef: order['full-reference'] || String(order.id || ''),
    });
  };

  const buildCaptadorData = (workorderRef = '') => ({
    tecnicoId:    String(user?.employeeId || ''),
    tecnicoNombre: user?.name || '',
    comision:     10,
    workorderRef,
  });

  return (
    <div className="p-4 space-y-4">

      {/* ── Búsqueda de cliente ── */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border space-y-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Nuevo contrato</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Captación · <span className="font-semibold text-green-600">10 €</span> por contrato firmado
          </p>
        </div>

        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={handleQueryChange}
            placeholder="Teléfono, nombre o email del cliente…"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 pr-10"
            autoComplete="off"
          />
          {searching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
            </div>
          )}
        </div>

        {results.length > 0 && (
          <div className="border border-gray-100 rounded-xl overflow-hidden divide-y divide-gray-50 shadow-sm">
            {results.map(c => (
              <button
                key={c.id}
                onClick={() => seleccionarCliente(c)}
                className="w-full text-left px-4 py-3 hover:bg-red-50 transition-colors"
              >
                <p className="ph-mask text-sm font-semibold text-gray-800">{c.clienteNombre}</p>
                <p className="ph-mask text-xs text-gray-400 mt-0.5">
                  {[c.phone, c.municipio].filter(Boolean).join(' · ')}
                </p>
                {c.assets?.length > 0 && (
                  <p className="text-xs text-gray-400 mt-0.5 italic">
                    {c.assets.length} equipo{c.assets.length !== 1 ? 's' : ''} registrado{c.assets.length !== 1 ? 's' : ''}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}

        {!searching && query.trim().length >= 2 && results.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-2">Sin resultados para «{query.trim()}»</p>
        )}
      </div>

      {/* ── Puestas en marcha del día ── */}
      <div>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Puestas en marcha hoy</p>
        {pmLoading ? (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
          </div>
        ) : pmOrders.length === 0 ? (
          <div className="bg-white rounded-2xl border p-6 text-center space-y-2">
            <p className="text-4xl">📋</p>
            <p className="text-sm font-semibold text-gray-700">No hay puestas en marcha hoy</p>
            <p className="text-xs text-gray-400">Busca al cliente arriba para crear un contrato</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pmOrders.map(order => {
              const eq = order.equipmentInfo || '';
              return (
                <div key={order.id} className="bg-white rounded-2xl border shadow-sm p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="ph-mask font-semibold text-gray-900 text-sm">{order.clientName || 'Sin nombre'}</p>
                        <span className="text-xs font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">PM</span>
                      </div>
                      {order.addressText && <p className="ph-mask text-xs text-gray-400 mt-0.5 truncate">{order.addressText}</p>}
                      {eq && <p className="text-xs text-gray-400 italic mt-0.5 truncate">{eq}</p>}
                    </div>
                    <p className="text-xs text-gray-400 flex-shrink-0 mt-0.5">{order['full-reference'] || `#${order.id}`}</p>
                  </div>
                  <button
                    onClick={() => abrirDesdeOrden(order)}
                    className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition-colors"
                  >
                    Crear contrato de mantenimiento
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ph-mask: formulario de oficina, todo él con datos del cliente — tapado entero en la grabación */}
      {modalConfig !== null && (
        <div className="ph-mask contents">
        <NuevoContratoModal
          onClose={() => setModalConfig(null)}
          onSaved={() => setModalConfig(null)}
          clienteInicial={modalConfig.clienteInicial}
          captadorData={buildCaptadorData(modalConfig.workorderRef)}
        />
        </div>
      )}
    </div>
  );
}
